/**
 * Slot-machine digit roll for the .no-main--count section.
 * Triggered when the section center enters the viewport center.
 * Each [data-count-num] span is split into per-digit reels (0-9 × 3 passes).
 * Leftmost digit settles last, rightmost settles first.
 */

const DIGIT_COUNT = 10;
const REEL_PASSES = 3;

export function initCountRoll(opts = {}) {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

  const { root = ".no-main--count", itemSel = ".no-main--count__item" } = opts;

  const section = document.querySelector(root);
  if (!section) return;

  const numEls = section.querySelectorAll("[data-count-num]");
  if (!numEls.length) return;

  numEls.forEach(buildReel);

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion) {
    section.querySelectorAll(".count-reel").forEach((reelEl) => {
      const track = reelEl.querySelector(".count-reel__track");
      if (!track) return;
      const target = parseInt(track.dataset.digit, 10);
      const cellH = measureCellHeight(track, reelEl);
      gsap.set(track, { y: -((REEL_PASSES - 1) * DIGIT_COUNT + target) * cellH });
    });
    return;
  }

  gsap.set(section.querySelectorAll(".count-reel__track"), { y: 0 });

  ScrollTrigger.create({
    trigger: section,
    start: "top 65%",
    once: true,
    onEnter() {
      const items = section.querySelectorAll(itemSel);
      items.forEach((item, itemIdx) => {
        const reels = item.querySelectorAll(".count-reel");
        reels.forEach((reelEl, reelIdx) => {
          const track = reelEl.querySelector(".count-reel__track");
          if (!track) return;

          const target = parseInt(track.dataset.digit, 10);
          const cellH = measureCellHeight(track, reelEl);
          const finalY = -((REEL_PASSES - 1) * DIGIT_COUNT + target) * cellH;

          // Leftmost digit (reelIdx 0) takes longest; rightmost settles first
          const offsetFromRight = reels.length - 1 - reelIdx;
          const itemDelay = itemIdx * 0.18;

          gsap.fromTo(
            track,
            { y: 0 },
            {
              y: finalY,
              duration: 0.85 + offsetFromRight * 0.1,
              delay: itemDelay,
              ease: "power3.out",
            },
          );
        });
      });
    },
  });
}

/**
 * Sub-pixel cell height. offsetHeight rounds to an integer, and that rounding
 * accumulates across ~20+ cells (REEL_PASSES × DIGIT_COUNT) so the reel lands
 * a few pixels off-center and the digit gets clipped. getBoundingClientRect
 * keeps the fractional height so the final cell settles exactly centered.
 */
function measureCellHeight(track, reelEl) {
  const cell = track.firstElementChild;
  const h = (cell || reelEl).getBoundingClientRect().height;
  return h || (cell || reelEl).offsetHeight;
}

function buildReel(el) {
  const numStr = el.dataset.countNum;
  if (!numStr) return;

  el.setAttribute("aria-hidden", "true");
  el.innerHTML = "";

  [...numStr].forEach((ch) => {
    const digit = parseInt(ch, 10);

    if (isNaN(digit)) {
      const staticEl = document.createElement("span");
      staticEl.className = "count-static";
      staticEl.textContent = ch;
      el.appendChild(staticEl);
      return;
    }

    const reel = document.createElement("span");
    reel.className = "count-reel";

    const track = document.createElement("span");
    track.className = "count-reel__track";
    track.dataset.digit = digit;

    for (let pass = 0; pass < REEL_PASSES; pass++) {
      for (let d = 0; d < DIGIT_COUNT; d++) {
        const cell = document.createElement("span");
        cell.className = "count-reel__cell";
        cell.textContent = d;
        track.appendChild(cell);
      }
    }

    reel.appendChild(track);
    el.appendChild(reel);
  });
}
